import {createReducer, on} from '@ngrx/store';
import * as StoreSettingsActions from './settings.actions';
import {IStoreSettings, IUserSettings} from '../models/settings-models';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {
  IEmailSettings,
  IOnHandNotZeroDisableRule,
  IOnHandZeroDisableRule,
  ITableNavSettings,
  TLineColour
} from '../../settings-new/models';

/**
 * TODO TECHNICAL DEBT export interface SettingsState extends IInitialState {}
 * This needs to be looked at - cleaned and sorted
 * Also there are various actions, we can add a loading interface here to toggle each settings loading separate
 *
 */
export interface SettingsState {
  errors: IGlobalError[];
  isLineColoursLoading: boolean;
  isOpenOrderSettingsLoading: boolean;
  isSelectedUserEmailSettingsLoading: boolean;
  isSubmittingEmailSettings: boolean;
  isSubmittingStoreSettings: boolean;
  isUserEmailSettingsLoading: boolean;
  selectedUserEmailSettings: IEmailSettings;
  storeSettings: Record<string, IStoreSettings>;
  userSettings: IUserSettings;
  usersEmailSettings: Record<string, Record<string, IEmailSettings>>;

}

export const initialOptionsState: SettingsState = {
  isLineColoursLoading: false,
  errors: [],
  storeSettings: {},
  isSubmittingStoreSettings: false,
  userSettings: {} as IUserSettings,
  isUserEmailSettingsLoading: false,
  isSubmittingEmailSettings: false,
  usersEmailSettings: {},
  isSelectedUserEmailSettingsLoading: false,
  selectedUserEmailSettings: {} as IEmailSettings,
  isOpenOrderSettingsLoading: false,
};

export const SettingsReducer = createReducer(
  initialOptionsState,

  //#region Table Settings
  on(StoreSettingsActions.setTableNavSettingsSuccess, (state, {settings, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      tableNavigationSettings: settings,
    },
  })),
  on(StoreSettingsActions.getTableSettingsSuccess, (state, {tableNavSettings, fitToGrid, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      tableNavigationSettings: tableNavSettings,
      fitToGrid: fitToGrid,
    },
  })),
  on(StoreSettingsActions.setTableNavSettingsFailure, (state, {error, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      tableNavigationSettings: {} as ITableNavSettings,
    },
    errors: [...state.errors, error],
  })),
  on(StoreSettingsActions.setFitToGridSuccess, (state, {fitToGrid}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      fitToGrid: fitToGrid,
    },
  })),
  on(StoreSettingsActions.toggleFitToGrid, (state) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      fitToGrid: !state.userSettings.fitToGrid,
    },
  })),
  //endregion

  //#region Item Disabling and Enabling Rules
  //#region Disabling Rules
  on(StoreSettingsActions.getItemDisablingRulesSuccess, (state, {rulesDoc, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        disabledRules: rulesDoc,
      },
    },
  })),
  on(StoreSettingsActions.getItemDisablingRulesFailure, (state, {error, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        disabledRules: {onHandZero: {} as IOnHandZeroDisableRule, onHandNotZero: {} as IOnHandNotZeroDisableRule},
      },
    },
    errors: [...state.errors, error],
  })),
  //endregion
  //#region Enabling Rules
  on(StoreSettingsActions.getItemEnablingRulesSuccess, (state, {rulesDoc, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        enabledRules: rulesDoc,
      },
    },
  })),
  on(StoreSettingsActions.getItemEnablingRulesFailure, (state, {error, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        enabledRules: {},
      },
    },
    errors: [...state.errors, error],
  })),
  on(StoreSettingsActions.updateEnablingSettings, (state) => ({
    ...state,
    isSubmittingStoreSettings: true,
  })),
  //endregion
  //endregion

  //#region Line Colour
  on(StoreSettingsActions.getLineColour, (state) => ({
    ...state,
    isLineColoursLoading: true,
  })),
  on(StoreSettingsActions.getLineColourSuccess, (state, {colour, store}) => ({
    ...state,
    isLineColoursLoading: false,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        lineColour: colour,
      },
    },
  })),

  on(StoreSettingsActions.getLineColourFailure, (state, {error, store}) => ({
    ...state,
    isLineColoursLoading: false,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        lineColour: {} as TLineColour,
      },
    },
    errors: [...state.errors, error],
  })),
  //endregion

  //#region Preview Columns
  //#region Stock Manager
  on(StoreSettingsActions.setStockManagerPreviewColumnsSuccess, (state, {columnData, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      stockManagerColPreviewSettings: columnData,
    },
  })),
  on(StoreSettingsActions.setStockManagerPreviewColumnsFailure, (state, {error, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      stockManagerColPreviewSettings: {},
    },
    errors: [...state.errors, error],
  })),
  on(StoreSettingsActions.getStockManagerColPreviewSettingsSuccess, (state, {colPreview, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      stockManagerColPreviewSettings: colPreview,
    },
  })),
  //endregion
  //#region NGP Report
  on(StoreSettingsActions.getNGPReportColPreviewSuccess, (state, {store, colPreview}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      ngpReportColPreviewSettings: colPreview,
    },
  })),
  on(StoreSettingsActions.setNGPReportColPreviewSettingsSuccess, (state, {columnData, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      ngpReportColPreviewSettings: columnData,
    },
  })),
  on(StoreSettingsActions.setNGPReportColPreviewSettingsFailure, (state, {error, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      ngpReportColPreviewSettings: {},
    },
    errors: [...state.errors, error],
  })),
  on(StoreSettingsActions.getNGPReportColPreviewFailure, (state, {errors, store}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      [store.storeId]: {
        ...state.userSettings[store.storeId],
        ngpReportColPreviewSettings: null,
      },
    },
    errors: [...state.errors, ...errors],
  })),
  //endregion
  //endregion

  //#region get Stock Edit Threshold
  on(StoreSettingsActions.getStockEditThresholdsSuccess, (state, {thresholds, storeId}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [storeId]: {
        ...state.storeSettings[storeId],
        priceThresholds: thresholds,
      },
    },
  })),
  on(StoreSettingsActions.getStockEditThresholdsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  //#endregion

  //#region Get Price bands
  on(StoreSettingsActions.getPriceBandsSuccess, (state, {priceBands, storeId}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [storeId]: {
        ...state.storeSettings[storeId],
        priceBands,
      },
    },
  })),
  on(StoreSettingsActions.getPriceBandsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  //#endregion

  //#region Email Settings
  on(StoreSettingsActions.getUserEmailSettings, (state) => ({
    ...state,
    isSelectedUserEmailSettingsLoading: true,
    selectedUserEmailSettings: {} as IEmailSettings,
  })),
  on(StoreSettingsActions.getUserEmailSettingsSuccess, (state, {settings}) => ({
    ...state,
    isSelectedUserEmailSettingsLoading: false,
    selectedUserEmailSettings: settings,
  })),
  on(StoreSettingsActions.getUserEmailSettingsFailure, (state, {error}) => ({
    ...state,
    isSelectedUserEmailSettingsLoading: false,
    selectedUserEmailSettings: {} as IEmailSettings,
    errors: [...state.errors, error],
  })),
  on(StoreSettingsActions.getAllUserEmailSettings, (state) => ({
    ...state,
    isUserEmailSettingsLoading: true,
  })),
  on(StoreSettingsActions.getAllUserEmailSettingsSuccess, (state, {usersEmailSettings, store}) => ({
    ...state,
    usersEmailSettings: {
      ...state.usersEmailSettings,
      [store.storeId]: usersEmailSettings,
    },
    isUserEmailSettingsLoading: false,
  })),
  on(StoreSettingsActions.getAllUserEmailSettingsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isUserEmailSettingsLoading: false,
  })),
  on(StoreSettingsActions.testEmail, (state) => ({
    ...state,
    isUserEmailSettingsLoading: true,
    isSubmittingEmailSettings: true,
  })),
  on(StoreSettingsActions.testEmailSuccess, (state) => ({
    ...state,
    isUserEmailSettingsLoading: false,
    isSubmittingEmailSettings: false,
  })),
  on(StoreSettingsActions.testEmailFailure, (state) => ({
    ...state,
    isUserEmailSettingsLoading: false,
    isSubmittingEmailSettings: false,
  })),
  //endregion

  //#region User Settings
  on(StoreSettingsActions.updateAllUserSettings, (state) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      isSubmitting: true,
    },
  })),
  on(StoreSettingsActions.updateAllUserSettingsSuccess, (state, {
    fitToGrid,
    ngpPreviewColumns,
    stockPreviewColumns,
    tableNavSettings,
  }) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      fitToGrid,
      ngpReportColPreviewSettings: ngpPreviewColumns,
      stockManagerColPreviewSettings: stockPreviewColumns,
      tableNavigationSettings: tableNavSettings,
      isSubmitting: false,
    },
  })),
  on(StoreSettingsActions.updateAllUserSettingsFailure, (state, {error}) => ({
    ...state,
    userSettings: {
      ...state.userSettings,
      isSubmitting: false,
    },
    errors: [...state.errors, error],
  })),
  //#endregion

  //#region On Hand Settings
  on(StoreSettingsActions.updateOnHandSettings, (state) => ({
    ...state,
    isSubmittingStoreSettings: true,
  })),
  on(StoreSettingsActions.updateOnHandSettingsSuccess, (state) => ({
    ...state,
    isSubmittingStoreSettings: false,
  })),
  on(StoreSettingsActions.updateOnHandSettingsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isSubmittingStoreSettings: false,
  })),
  //#endregion

  //#region Stock Edit Thresholds
  on(StoreSettingsActions.updateStockEditThresholds, (state) => ({
    ...state,
    isSubmittingStoreSettings: true,
  })),
  on(StoreSettingsActions.updateStockEditThresholdsSuccess, (state) => ({
    ...state,
    isSubmittingStoreSettings: false,
  })),
  on(StoreSettingsActions.updateStockEditThresholdsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isSubmittingStoreSettings: false,
  })),
  //#endregion

  //#region Open Order Settings
  on(StoreSettingsActions.getOpenOrderSettings, (state) => ({
    ...state,
    isOpenOrderSettingsLoading: true,
  })),
  on(StoreSettingsActions.updateOpenOrderSettingsSuccess, (state, {openOrderSettings, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        openOrderSettings,
      },
    },
  })),
  on(StoreSettingsActions.getOpenOrderSettingsSuccess, (state, {openOrderSettings, store}) => ({
    ...state,
    isOpenOrderSettingsLoading: false,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        openOrderSettings,
      },
    },
  })),
  on(StoreSettingsActions.getOpenOrderSettingsFailure, (state, {error}) => ({
    ...state,
    isOpenOrderSettingsLoading: false,
    errors: [...state.errors, error],
  })),
  //endregion

  //#region Override Settings
  on(StoreSettingsActions.getOverridesSuccess, (state, {overrides, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        overrides: {
          ...state.storeSettings[store.storeId]?.overrides,
          orderBlankSupplierCode: overrides.orderBlankSupplierCode,
          shelfTalkersEnabled: overrides.shelfTalkersEnabled,
        },
      },
    },
  })),
  on(StoreSettingsActions.setSupplierOverrideSuccess, (state, {supplierOverRide, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        overrides: {
          ...state.storeSettings[store.storeId]?.overrides,
          orderBlankSupplierCode: supplierOverRide,
        },
      },
    },
  })),
  on(StoreSettingsActions.setShelfTalkerOverrideSuccess, (state, {shelfTalkerOverRide, store}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        overrides: {
          ...state.storeSettings[store.storeId]?.overrides,
          shelfTalkersEnabled: shelfTalkerOverRide,
        },
      },
    },
  })),
  //endregion

  //region Add Stock Item Rules
  on(StoreSettingsActions.getAddStockItemRulesSuccess, (state, {store, prefix, suffix, digits}) => ({
    ...state,
    storeSettings: {
      ...state.storeSettings,
      [store.storeId]: {
        ...state.storeSettings[store.storeId],
        addStockItemRules: {
          prefix: prefix,
          suffix: suffix,
          digits: digits,
        },
      },
    },
  })),
  on(StoreSettingsActions.setAddStockItemRulesSuccess, (state, {storeId, prefix, suffix, digits}) => ({
      ...state,
      storeSettings: {
        ...state.storeSettings,
        [storeId]: {
          ...state.storeSettings?.[storeId],
          addStockItemRules: {prefix, suffix, digits},
        },
      },
    }),
  ),

//endregion
);
